import type { FramePreset, FrameSettings, FrameSettingsPatch } from '../types';

export const DEFAULT_SETTINGS: FrameSettings = {
  borderWidth: 8,
  borderUnit: 'percent',
  borderColor: '#f8f8f6',
  borderOpacity: 100,
  aspectRatio: 'original',
  customAspectWidth: 4,
  customAspectHeight: 5,
  shadow: {
    enabled: false,
    strength: 26,
    blur: 24,
    offsetX: 0,
    offsetY: 10,
    color: '#111827'
  },
  corners: {
    enabled: false,
    linked: true,
    radius: 0,
    topLeft: 0,
    topRight: 0,
    bottomRight: 0,
    bottomLeft: 0
  },
  metadata: {
    enabled: false,
    position: 'bottom-left',
    fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
    fontSize: 28,
    color: '#23262d',
    customText: '',
    showCamera: true,
    showLens: true,
    showExposure: true,
    showDate: false,
    showLocation: false
  }
};

function withSettings(overrides: FrameSettingsPatch): FrameSettings {
  return {
    ...DEFAULT_SETTINGS,
    ...overrides,
    shadow: { ...DEFAULT_SETTINGS.shadow, ...overrides.shadow },
    corners: { ...DEFAULT_SETTINGS.corners, ...overrides.corners },
    metadata: { ...DEFAULT_SETTINGS.metadata, ...overrides.metadata }
  };
}

export const BUILT_IN_PRESETS: FramePreset[] = [
  {
    id: 'minimal-white',
    name: 'Minimal White',
    description: 'Clean gallery border',
    builtIn: true,
    settings: withSettings({ borderColor: '#f8f8f6', borderWidth: 8, borderUnit: 'percent' })
  },
  {
    id: 'minimal-black',
    name: 'Minimal Black',
    description: 'High contrast edge',
    builtIn: true,
    settings: withSettings({ borderColor: '#17191f', borderWidth: 7, borderUnit: 'percent' })
  },
  {
    id: 'shadow-white',
    name: 'Soft Shadow',
    description: 'White frame with depth',
    builtIn: true,
    settings: withSettings({
      borderColor: '#f7f5ef',
      borderWidth: 10,
      borderUnit: 'percent',
      shadow: { enabled: true, strength: 30, blur: 34, offsetY: 14 }
    })
  },
  {
    id: 'photographer-exif',
    name: 'Photographer',
    description: 'Metadata signature',
    builtIn: true,
    settings: withSettings({
      borderWidth: 12,
      borderUnit: 'percent',
      borderColor: '#fbfaf7',
      metadata: { enabled: true, position: 'bottom-left', fontSize: 26 }
    })
  },
  {
    id: 'instagram-square',
    name: 'Instagram',
    description: 'Square social crop',
    builtIn: true,
    settings: withSettings({
      aspectRatio: '1:1',
      borderWidth: 9,
      borderUnit: 'percent',
      shadow: { enabled: true, strength: 18, blur: 20, offsetY: 8 }
    })
  },
  {
    id: 'print-large',
    name: 'Print',
    description: 'Large lower margin',
    builtIn: true,
    settings: withSettings({
      borderWidth: 16,
      borderUnit: 'percent',
      borderColor: '#f4f1e9',
      metadata: { enabled: true, position: 'bottom-right', fontSize: 24 }
    })
  },
  {
    id: 'cinema-169',
    name: 'Cinema 16:9',
    description: 'Wide matte frame',
    builtIn: true,
    settings: withSettings({ aspectRatio: '16:9', borderColor: '#101217', borderWidth: 6 })
  },
  {
    id: 'story-916',
    name: 'Story',
    description: 'Vertical social frame',
    builtIn: true,
    settings: withSettings({ aspectRatio: '9:16', borderColor: '#f8f8f6', borderWidth: 7 })
  },
  {
    id: 'rounded-card',
    name: 'Rounded',
    description: 'Soft corners',
    builtIn: true,
    settings: withSettings({
      borderWidth: 7,
      shadow: { enabled: true, strength: 22, blur: 28, offsetY: 10 },
      corners: { enabled: true, linked: true, radius: 28, topLeft: 28, topRight: 28, bottomRight: 28, bottomLeft: 28 }
    })
  },
  {
    id: 'transparent-png',
    name: 'Transparent',
    description: 'PNG-friendly edge',
    builtIn: true,
    settings: withSettings({ borderOpacity: 0, borderWidth: 8, shadow: { enabled: true, strength: 30, blur: 30 } })
  }
];

export function cloneSettings(settings: FrameSettings): FrameSettings {
  return JSON.parse(JSON.stringify(settings)) as FrameSettings;
}
